import React, { useState, useEffect } from 'react';
import { getUserAvatarConfig, buildAvatarLayers, getAllStudentAssets } from '../services/avatarBuilderService';


/**
 * UserAvatar
 *
 * Displays a user's saved layered avatar if one exists in user_avatar_configs,
 * otherwise falls back to the first letter of their name.
 *
 * Props:
 *  - userId       {string}  required — the user's ID to look up avatar config
 *  - name         {string}  optional — user's name for fallback letter display
 *  - size         {string}  optional — tailwind size classes e.g. "w-10 h-10" (default "w-10 h-10")
 *  - textSize     {string}  optional — tailwind text size for letter fallback (default "text-sm")
 *  - className    {string}  optional — extra classes for the wrapper
 */
const UserAvatar = ({
  userId,
  name = '',
  size = 'w-10 h-10',
  textSize = 'text-sm',
  className = '',
}) => {
  const [layers, setLayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAvatar, setHasAvatar] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadAvatar = async () => {
      try {
        setLoading(true);
        const config = await getUserAvatarConfig(userId);

        if (cancelled) return;

        if (!config?.avatar_config) {
          setHasAvatar(false);
          setLayers([]);
          return;
        }

        const allAssets = await getAllStudentAssets();
        if (cancelled) return;

        const assetsByCategorySlug = allAssets?.reduce((acc, asset) => {
          const slug = asset?.avatar_asset_categories?.slug;
          if (!slug) return acc;
          if (!acc?.[slug]) acc[slug] = [];
          acc?.[slug]?.push(asset);
          return acc;
        }, {});

        const resolvedLayers = buildAvatarLayers(config?.avatar_config, assetsByCategorySlug);
        if (cancelled) return;

        setLayers(resolvedLayers);
        setHasAvatar(resolvedLayers?.length > 0);
      } catch (err) {
        if (!cancelled) {
          setHasAvatar(false);
          setLayers([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadAvatar();
    return () => { cancelled = true; };
  }, [userId]);

  const letter = name?.charAt(0)?.toUpperCase() || '?';

  if (loading) {
    return (
      <div
        className={`${size} rounded-full bg-primary/20 animate-pulse flex-shrink-0 ${className}`}
      />
    );
  }

  if (hasAvatar && layers?.length > 0) {
    return (
      <div
        className={`${size} rounded-full overflow-hidden relative flex-shrink-0 bg-muted/30 ${className}`}
      >
        {layers?.map((layer, idx) => (
          <img
            key={`${layer?.slug}-${idx}`}
            src={layer?.publicUrl}
            alt=""
            className="absolute inset-0 w-full h-full object-contain"
            style={{ zIndex: layer?.layer_order ?? idx }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        ))}
      </div>
    );
  }

  // Fallback: letter avatar
  return (
    <div
      className={`${size} rounded-full bg-primary text-primary-foreground flex items-center justify-center font-semibold flex-shrink-0 ${textSize} ${className}`}
    >
      {letter}
    </div>
  );
};

export default UserAvatar;
